import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { ChevronRight, Landmark, Plus } from "lucide-react";

import EmptyState from "../../components/data-table/EmptyState";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { useBankName } from "../../hooks/use-bank-name";
import { cn } from "../../utils/cn";
import { errorMessage } from "../../utils/errorMessage";
import AddBanks from "./AddBanks";
import BankDetail from "./BankDetail";

const BankDetails = ({ setOpenBankDetails, banks, businessID }: any) => {
  const [openAddBank, setOpenAddBank] = useState<boolean>(false);
  const [bankId, setBankId] = useState<string | null>(null);

  const { bankName, error } = useBankName();

  useEffect(() => {
    if (error) {
      toast.error(errorMessage(error?.message || error?.data?.message));
    }
  }, [error]);

  // Copy before sorting — Array.prototype.sort mutates in place, and `banks`
  // comes straight from the React Query cache.
  const sorted = [...(banks ?? [])].sort(
    (a: any, b: any) => Number(!!b.isDefault) - Number(!!a.isDefault)
  );

  return (
    <>
      <Dialog open onOpenChange={(next) => !next && setOpenBankDetails(false)}>
        <DialogContent className="font-manrope mds:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="font-sora">Bank accounts</DialogTitle>
            <DialogDescription>
              Settlements are paid into your default account.
            </DialogDescription>
          </DialogHeader>

          {sorted.length === 0 ? (
            <EmptyState
              title="No bank account added"
              description="Add a bank account so your payouts have somewhere to settle."
            />
          ) : (
            <ul className="space-y-3">
              {sorted.map((bank: any) => (
                <li key={bank._id}>
                  <button
                    type="button"
                    onClick={() => setBankId(bank._id)}
                    className={cn(
                      "flex w-full items-center gap-4 rounded-xl border p-4 text-left transition-colors hover:bg-muted",
                      bank.isDefault && "border-primary/40 bg-accent/50"
                    )}
                  >
                    <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-accent text-primary">
                      <Landmark className="size-5" />
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="truncate text-[14px] font-semibold text-foreground">
                          {bank.accountName || "Unnamed account"}
                        </span>
                        {bank.isDefault && (
                          <Badge className="shrink-0">Default</Badge>
                        )}
                      </span>
                      <span className="mt-0.5 block truncate text-[13px] font-medium text-muted-foreground">
                        {bank.accountNumber}
                        {bankName(bank.bankCode)
                          ? ` • ${bankName(bank.bankCode)}`
                          : ""}
                      </span>
                    </span>

                    <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <DialogFooter>
            <Button
              onClick={() => setOpenAddBank(true)}
              className="h-[52px] w-full gap-2 rounded-xl text-[15px] font-semibold"
            >
              <Plus className="size-4" />
              Add bank
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {openAddBank && (
        <AddBanks
          setOpenAddBank={setOpenAddBank}
          businessID={businessID}
          nested
        />
      )}

      {bankId && (
        <BankDetail
          setOpenBank={(next: boolean) => !next && setBankId(null)}
          bankId={bankId}
          banks={banks}
          businessID={businessID}
          nested
        />
      )}
    </>
  );
};

export default BankDetails;
